import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { getTimeEntries, getMonthInfo, getFlexBalance, getOverdueStatus, getVacationBalance, getMyTimesheetSubmissions } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, TrendingUp, AlertTriangle, Receipt, Palmtree, Eye, FileText } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';

export default function EmployeeDashboard() {
    const { t } = useTranslation();
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [monthInfo, setMonthInfo] = useState(null);
    const [entries, setEntries] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [overdueInfo, setOverdueInfo] = useState(null);
    const [vacationBalance, setVacationBalance] = useState(null);
    const [flexLimits, setFlexLimits] = useState({ min: -40, max: 80 });
    const [selectedSubmission, setSelectedSubmission] = useState(null);
    const [submissionEntries, setSubmissionEntries] = useState([]);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [stats, setStats] = useState({
        totalHours: 0,
        expectedHours: 0,
        regularHours: 0,
        overtimeHours: 0,
        deviationHours: 0,
        pendingApproval: 0,
        approved: 0
    });

    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [monthRes, entriesRes, flexRes, overdueRes, vacationRes, submissionsRes] = await Promise.all([
                getMonthInfo(currentMonth, currentYear),
                getTimeEntries({ month: currentMonth, year: currentYear }),
                getFlexBalance(),
                getOverdueStatus(),
                getVacationBalance(),
                getMyTimesheetSubmissions()
            ]);

            setMonthInfo(monthRes.data);
            setSubmissions(submissionsRes.data || []);
            setEntries(entriesRes.data);
            setOverdueInfo(overdueRes.data);
            setVacationBalance(vacationRes.data);

            if (user) {
                updateUser({ ...user, flex_balance: flexRes.data.balance });
            }
            if (flexRes.data) {
                setFlexLimits({
                    min: flexRes.data.flex_limit_min ?? -40,
                    max: flexRes.data.flex_limit_max ?? 80,
                });
            }

            const totalHours = entriesRes.data.reduce((sum, e) => sum + e.hours, 0);
            const regularHours = entriesRes.data
                .filter(e => !e.deviation_type_id && !e.overtime_type)
                .reduce((sum, e) => sum + e.hours, 0);
            const overtimeHours = entriesRes.data
                .filter(e => e.overtime_type)
                .reduce((sum, e) => sum + e.hours, 0);
            const deviationHours = entriesRes.data
                .filter(e => e.deviation_type_id)
                .reduce((sum, e) => sum + e.hours, 0);
            const pendingApproval = entriesRes.data.filter(e => !e.approved).length;
            const approved = entriesRes.data.filter(e => e.approved).length;

            setStats({
                totalHours,
                expectedHours: monthRes.data.expected_hours,
                regularHours,
                overtimeHours,
                deviationHours,
                pendingApproval,
                approved
            });
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGoToOverdueMonth = () => {
        // Navigate to timesheet and store the target month in sessionStorage
        if (overdueInfo?.overdue) {
            sessionStorage.setItem('targetMonth', overdueInfo.month);
            sessionStorage.setItem('targetYear', overdueInfo.year);
        }
        navigate('/dashboard/log-reports');
    };

    const viewSubmissionDetails = async (submission) => {
        setSelectedSubmission(submission);
        try {
            const res = await getTimeEntries({ month: submission.month, year: submission.year });
            setSubmissionEntries(res.data || []);
        } catch (error) {
            console.error('Failed to load entries:', error);
            setSubmissionEntries([]);
        }
        setViewDialogOpen(true);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }

    const progressPercent = stats.expectedHours > 0 
        ? Math.min((stats.totalHours / stats.expectedHours) * 100, 100) 
        : 0;

    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];

    return (
        <div className="space-y-8" data-testid="employee-dashboard">
            {/* Header */}
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-start">
                <div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">
                        {t('admin.dashboard.welcome', { name: user?.name?.split(' ')[0] })}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {t('employee_dashboard.overview_subtitle', { month: new Date().toLocaleDateString('en-US', { month: 'long' }), year: new Date().getFullYear() })}
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        className="uppercase tracking-wider"
                        onClick={() => navigate('/dashboard/log-reports')}
                        data-testid="go-to-timesheet-btn"
                    >
                        <Calendar className="w-4 h-4 mr-2" />
                        {t('employee_dashboard.timesheet_button')}
                    </Button>
                </div>
            </div>

            {/* Overdue Warning */}
            {overdueInfo?.overdue && (
                <Card className="border-2 border-destructive shadow-none bg-red-50" data-testid="overdue-warning">
                    <CardContent className="p-6 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-destructive flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6 text-white" strokeWidth={1.5} />
                            </div>
                            <div>
                                <h3 className="font-heading text-lg font-bold text-destructive">
                                    {t('employee_dashboard.overdue_title')}
                                </h3>
                                <p className="text-sm text-red-600">
                                    {t('employee_dashboard.overdue_desc', { month: monthNames[overdueInfo.month - 1], year: overdueInfo.year })}
                                    {overdueInfo.entries_count > 0 && (
                                        <span> {t('employee_dashboard.overdue_entries', { count: overdueInfo.entries_count })}</span>
                                    )}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="destructive"
                            className="uppercase tracking-wider"
                            onClick={handleGoToOverdueMonth}
                            data-testid="complete-overdue-btn"
                        >
                            {t('employee_dashboard.complete_now')}
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-6">
                {/* Hours Progress */}
                <Card className="border border-border shadow-none">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Clock className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('employee_dashboard.stat_hours')}
                            </span>
                        </div>
                        <div className="stat-value tabular-nums">
                            {stats.totalHours}
                            <span className="text-lg text-muted-foreground font-normal">
                                /{stats.expectedHours}h
                            </span>
                        </div>
                        <div className="mt-4 h-2 bg-muted">
                            <div 
                                className="h-full bg-foreground transition-all duration-500"
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>
                        <p className="stat-label">
                            {t('common.percent_complete', { percent: Math.round(progressPercent) })}
                        </p>
                    </CardContent>
                </Card>

                {/* Flex Balance */}
                <Card className="border border-border shadow-none">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <TrendingUp className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('employee_dashboard.stat_flex')}
                            </span>
                        </div>
                        <div className={`stat-value tabular-nums ${user?.flex_balance >= 0 ? 'flex-positive' : 'flex-negative'}`}>
                            {user?.flex_balance > 0 ? '+' : ''}{user?.flex_balance || 0}h
                        </div>
                        <p className="stat-label">
                            {t('admin.dashboard.flex_balance_limits', { max: flexLimits.max ?? 80, min: flexLimits.min ?? -40 })}
                        </p>
                    </CardContent>
                </Card>

                {/* Vacation Days */}
                <Card className="border border-border shadow-none">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Palmtree className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('employee_dashboard.stat_vacation')}
                            </span>
                        </div>
                        <div className="stat-value tabular-nums flex-positive">
                            {vacationBalance?.total_available || 0}d
                        </div>
                        <p className="stat-label">
                            {vacationBalance?.vacation_days || 0} earned
                            {vacationBalance?.advance_vacation_days > 0 && vacationBalance?.years_employed < 5 && (
                                <> + {vacationBalance.advance_vacation_days - vacationBalance.advance_vacation_used} advance</>
                            )}
                        </p>
                    </CardContent>
                </Card>

                {/* Overtime */}
                <Card className="border border-border shadow-none">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Clock className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('employee_dashboard.stat_overtime')}
                            </span>
                        </div>
                        <div className="stat-value tabular-nums">
                            {stats.overtimeHours}h
                        </div>
                        <p className="stat-label">
                            {t('employee_dashboard.stat_this_month')}
                        </p>
                    </CardContent>
                </Card>

                {/* Status */}
                <Card className="border border-border shadow-none">
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <AlertTriangle className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                            <span className="text-xs text-muted-foreground uppercase tracking-widest">
                                {t('employee_dashboard.stat_status')}
                            </span>
                        </div>
                        <div className="stat-value tabular-nums">
                            {stats.pendingApproval}
                        </div>
                        <p className="stat-label">
                            {t('employee_dashboard.stat_pending_approval')}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* My Timesheet Submissions */}
            <Card className="border border-border shadow-none">
                <CardContent className="p-0">
                    <div className="p-4 border-b border-border">
                        <h3 className="font-heading text-lg font-bold flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            {t('employee_dashboard.my_submissions_title')}
                        </h3>
                    </div>
                    {submissions.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground">
                            <Calendar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                            <p>{t('employee_dashboard.no_submissions')}</p>
                            <Button
                                variant="outline"
                                className="mt-4 uppercase tracking-wider text-xs"
                                onClick={() => navigate('/dashboard/log-reports')}
                            >
                                {t('employee_dashboard.start_logging')}
                            </Button>
                        </div>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>{t('employee_dashboard.col_period')}</th>
                                    <th>{t('employee_dashboard.col_hours')}</th>
                                    <th>{t('employee_dashboard.col_miles')}</th>
                                    <th>{t('employee_dashboard.col_entries')}</th>
                                    <th>{t('employee_dashboard.col_submitted')}</th>
                                    <th>{t('employee_dashboard.col_status')}</th>
                                    <th>{t('employee_dashboard.col_actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {submissions.slice(0, 12).map((sub) => (
                                    <tr key={sub.id}>
                                        <td className="font-medium">
                                            {monthNames[sub.month - 1]} {sub.year}
                                        </td>
                                        <td className="font-mono">{sub.total_hours}h</td>
                                        <td className="font-mono">{sub.total_miles || 0}</td>
                                        <td className="font-mono">{sub.entries_count}</td>
                                        <td className="text-sm text-muted-foreground">
                                            {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td>
                                            <span className={`px-2 py-1 text-xs uppercase tracking-wider ${
                                                sub.status === 'approved' 
                                                    ? 'bg-green-600 text-white' 
                                                    : sub.status === 'rejected'
                                                    ? 'bg-destructive text-white'
                                                    : sub.status === 'submitted'
                                                    ? 'bg-amber-500 text-white'
                                                    : 'bg-muted text-muted-foreground'
                                            }`}>
                                                {sub.status === 'submitted' ? t('employee_dashboard.status_pending') : sub.status}
                                            </span>
                                        </td>
                                        <td>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => viewSubmissionDetails(sub)}
                                                data-testid={`view-submission-${sub.id}`}
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            {/* Submission Details Dialog */}
            <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">
                            {selectedSubmission && t('employee_dashboard.details_title', { month: monthNames[selectedSubmission.month - 1], year: selectedSubmission.year })}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedSubmission && (
                        <div className="space-y-4">
                            {/* Summary */}
                            <div className="grid grid-cols-4 gap-4 p-4 bg-muted">
                                <div className="text-center">
                                    <p className="font-mono text-2xl font-bold">{selectedSubmission.total_hours}h</p>
                                    <p className="text-xs text-muted-foreground uppercase">{t('employee_dashboard.summary_total_hours')}</p>
                                </div>
                                <div className="text-center">
                                    <p className="font-mono text-2xl font-bold">{selectedSubmission.total_miles || 0}</p>
                                    <p className="text-xs text-muted-foreground uppercase">{t('employee_dashboard.summary_total_miles')}</p>
                                </div>
                                <div className="text-center">
                                    <p className="font-mono text-2xl font-bold">{selectedSubmission.entries_count}</p>
                                    <p className="text-xs text-muted-foreground uppercase">{t('employee_dashboard.summary_entries')}</p>
                                </div>
                                <div className="text-center">
                                    <p className={`text-sm font-medium ${
                                        selectedSubmission.status === 'approved' ? 'text-green-600' : 
                                        selectedSubmission.status === 'rejected' ? 'text-destructive' : 'text-amber-600'
                                    }`}>
                                        {selectedSubmission.status === 'submitted' ? t('employee_dashboard.summary_pending_review') : selectedSubmission.status?.toUpperCase()}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {t('employee_dashboard.summary_sent')} {selectedSubmission.submitted_at ? new Date(selectedSubmission.submitted_at).toLocaleDateString() : '-'}
                                    </p>
                                    {selectedSubmission.reviewed_at && (
                                        <p className="text-xs text-muted-foreground">
                                            {selectedSubmission.status === 'approved' ? t('employee_dashboard.summary_approved') : t('employee_dashboard.summary_reviewed')}: {new Date(selectedSubmission.reviewed_at).toLocaleDateString()}
                                            {selectedSubmission.reviewer_name && ` by ${selectedSubmission.reviewer_name}`}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Rejection Comment */}
                            {selectedSubmission.rejection_comment && (
                                <div className="p-4 bg-red-50 border border-destructive text-destructive">
                                    <p className="text-xs uppercase tracking-widest mb-1">{t('employee_dashboard.rejection_comment_label')}</p>
                                    <p>{selectedSubmission.rejection_comment}</p>
                                </div>
                            )}

                            {/* Entries Table */}
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>{t('common.date')}</th>
                                        <th>{t('employee_dashboard.col_project_deviation')}</th>
                                        <th>{t('employee_dashboard.col_activity')}</th>
                                        <th>{t('common.hours')}</th>
                                        <th>{t('common.miles')}</th>
                                        <th>{t('employee_dashboard.col_type')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {submissionEntries.map((entry) => (
                                        <tr key={entry.id}>
                                            <td className="font-mono">{entry.date}</td>
                                            <td>{entry.project_name || entry.deviation_name || '-'}</td>
                                            <td>{entry.activity || '-'}</td>
                                            <td className="font-mono">{entry.hours}h</td>
                                            <td className="font-mono">{entry.miles_km || '-'}</td>
                                            <td>
                                                {entry.deviation_type_id && (
                                                    <span className="time-entry-pill deviation">{t('common.deviation')}</span>
                                                )}
                                                {entry.overtime_type === 'standard' && (
                                                    <span className="time-entry-pill overtime">{t('timesheet.entry_type.ot_standard')}</span>
                                                )}
                                                {entry.overtime_type === 'special' && (
                                                    <span className="time-entry-pill overtime">{t('timesheet.entry_type.ot_special')}</span>
                                                )}
                                                {entry.overtime_type === 'flex_save' && (
                                                    <span className="time-entry-pill flex-deposit">{t('common.flex_deposit')}</span>
                                                )}
                                                {!entry.deviation_type_id && !entry.overtime_type && (
                                                    <span className="time-entry-pill">{t('common.regular')}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
